// components/CartModal.jsx
import React from 'react';
import { X, ShoppingCart, Trash2, Loader } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useContext, useState, useEffect } from 'react';
import { ProductContext } from '../context/ProductContext';
import axios from 'axios';

const CartModal = ({ showCart, setShowCart, getImageUrl, jwtToken, onOrderSuccess }) => {
  const apiBaseUrl = import.meta.env.VITE_API_URL;

  const {
    cart,
    removeFromCart,
    cartMessage,
    setCart,
    convertToUSD,
    fetchExchangeRate,
    exchangeRate  // Add this
  } = useCart(); // Add cartMessage here

  const { products, setProducts } = useContext(ProductContext);
  const [removingItems, setRemovingItems] = useState({}); // Add this line
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');

  // Fetch exchange rate when component mounts
  useEffect(() => {
    if (jwtToken) {
      fetchExchangeRate(jwtToken);
    }
  }, [jwtToken]);

  // Price display helper function
  const formatPrice = (price, currency, quantity) => {
    if (!currency || !price) {
      console.log('Missing price or currency:', { price, currency });
      return '$0.00';
    }

    if (currency === 'KHR') {
      const totalKHR = price * quantity;
      const totalUSD = convertToUSD(totalKHR, 'KHR');
      return (
        <>
          <span>៛{totalKHR.toLocaleString()}</span>
          <span className="text-xs text-gray-500 ml-1">
            (${totalUSD.toFixed(2)})
          </span>
        </>
      );
    }
    return `$${(price * quantity).toFixed(2)}`;
  };

  // Calculate total in USD
  const total = cart.reduce((sum, item) => {
    if (!item.price || !item.price_ccy) return sum;
    
    let priceInUSD = 0;
    if (item.price_ccy === 'KHR') {
      priceInUSD = convertToUSD(item.price, 'KHR');
    } else {
      priceInUSD = item.price; // Already in USD
    }
  
    return Number(sum) + (Number(priceInUSD) * Number(item.quantity));
  }, 0);

  // components/CartModal.jsx
  const handleRemoveItem = async (item) => {
    const itemKey = `${item.id}-${item.size}-${item.color}`;
    setRemovingItems(prev => ({ ...prev, [itemKey]: true }));

    await new Promise(resolve => setTimeout(resolve, 300));
    removeFromCart(item, products, setProducts);

    setRemovingItems(prev => ({ ...prev, [itemKey]: false }));
  };

  // Add handleCheckout function
  const handleCheckout = async () => {
    if (!phone || !address) {
      alert('Please fill in required customer information');
      return;
    }

    if (!jwtToken) {
      alert('Authentication token not found. Please try again.');
      return;
    }

    setIsCheckingOut(true);

    try {
      // Compute subtotal (sum of quantity × price) in USD for API V2
      const newSubtotalPrice = cart.reduce((sum, item) => {
        if (!item.price || !item.price_ccy) return sum;
        let priceInUSD = item.price_ccy === 'KHR' ? convertToUSD(item.price, 'KHR') : item.price;
        return Number(sum) + Number(priceInUSD) * Number(item.quantity);
      }, 0);

      // Prepare batch orders data (V2: no status, ordering_id, delivery, cod)
      const batchOrders = cart.map(cartItem => {
        const product = products.find(p => p.fashion_id === cartItem.id);
        const variationData = product?.data.find(d =>
          d.color_name === cartItem.color && d.size_name === cartItem.size
        );

        if (!product || !variationData) {
          throw new Error(`Product data not found for item ${cartItem.id}`);
        }

        return {
          fashion_id: cartItem.id,
          category_name: variationData.category_name || '',
          size_name: cartItem.size,
          color_name: cartItem.color,
          order_quantity: cartItem.quantity,
          image_url: product.image_url,
          customer_phone_number: phone,
          customer_location: address,
          remark: note || null,
          inventory_status: product.inventory_status || 'Pending',
          inventory_id: variationData.inventory_id || 0
        };
      });

      // Customer details V2: new_subtotal_price, new_delivery_fee, new_paid_amount (no total_price, delivery_fee, ordering_id)
      const customerDetails = {
        customer_phone_number: phone,
        customer_location: address,
        remark: note || '',
        new_subtotal_price: Number(newSubtotalPrice.toFixed(2)),
        new_delivery_fee: 0,
        new_paid_amount: 0
      };

      // Prepare the complete request body
      const requestBody = {
        orders: batchOrders,
        customer_details: customerDetails
      };

      console.log('Request body:', requestBody);
      // Make the API call
      const response = await axios.post(
        `${apiBaseUrl}/api/v1/share_catalog/placeOrder`,
        requestBody,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 201) {
        setCart([]);
        setShowCart(false);
        onOrderSuccess();
      } else {
        alert('Failed to process orders. Please try again.');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Error placing order: ${error.response?.data?.message || error.message || 'Please try again.'}`);
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (!showCart) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg sm:text-xl font-semibold">Shopping Cart</h3>
            <button
              onClick={() => setShowCart(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 text-sm sm:text-base">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-4 max-h-80 sm:max-h-96 overflow-y-auto">
                {cart.map((item) => (
                  <div key={`${item.id}-${item.size}-${item.color}`}
                    className={`flex gap-4 py-4 border-t ${removingItems[`${item.id}-${item.size}-${item.color}`]
                      ? 'opacity-50 transition-opacity duration-300'
                      : ''
                      }`}
                  >
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <h4 className="font-medium text-gray-900 text-sm sm:text-base">
                          {item.name}
                        </h4>
                        <button
                          onClick={() => handleRemoveItem(item)}
                          disabled={removingItems[`${item.id}-${item.size}-${item.color}`]}
                          className={`p-1 rounded-full ${removingItems[`${item.id}-${item.size}-${item.color}`]
                            ? 'bg-gray-100 cursor-not-allowed'
                            : 'hover:bg-gray-100'
                            }`}
                        >
                          {removingItems[`${item.id}-${item.size}-${item.color}`] ? (
                            <Loader className="animate-spin h-4 w-4 text-gray-500" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500 transition-colors" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        {item.color} • {item.size} • Qty: {item.quantity}
                      </p>
                      <div className="font-medium mt-2 text-sm sm:text-base">
                        {formatPrice(item.price, item.price_ccy, item.quantity)}
                      </div>
                    </div>
                  </div>
                ))}

              </div>

              {/* Enhanced Customer Information Form */}
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <h4 className="font-medium text-gray-700">ព័ត៌មានអតិថិជន</h4>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    លេខទូរស័ព្ទ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="បញ្ចូលលេខទូរស័ព្ទរបស់អ្នក"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    អាសយដ្ឋានដឹកជញ្ជូន <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="បញ្ចូលអាសយដ្ឋានដឹកជញ្ជូន"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">ចំណាំ</label>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Add any notes here"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </div>

              {/* Total and Checkout Button */}
              <div className="space-y-4 pt-4">
              <div className="flex justify-between items-center text-lg">
  <span className="font-medium">Total / សរុប</span>
  <div className="text-right">
    <span className="font-bold text-xl">${total.toFixed(2)}</span>
    {cart.some(item => item.price_ccy === 'KHR') && (
      <p className="text-xs text-gray-500">
        Rate: ៛{exchangeRate?.toLocaleString()} = $1
      </p>
    )}
  </div>
</div>
                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut || !phone || !address}
                  className={`
                    w-full py-3 rounded-xl text-base font-medium
                    transition-all duration-200 relative
                    ${isCheckingOut || !phone || !address
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'}
                  `}
                >
                  {isCheckingOut ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Place Order / បញ្ជាទិញ'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {cartMessage.text && (
        <div className={`
          fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-opacity duration-300
          ${cartMessage.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
        `}>
          {cartMessage.text}
        </div>
      )}
    </div>
  );
};

export default CartModal;